/**
 * JSON-based themes registry
 * This file provides a unified interface to JSON themes
 */

import fs from 'fs';
import path from 'path';
import { fileURLToPath } from 'url';
import {
  isValidThemeConfig,
  type ThemeConfigJson,
} from '@json-to-office/shared-docx';
import { ensureThemeDefaults } from '../../themes/defaults';

// Import theme JSON files directly. Every bundled theme is imported here, so
// which themes exist is decided by code, not by whether `dist/` happens to be
// built and fresh — `apex` and `devportal` used to be reachable only through
// the filesystem scan below, which meant a stale build silently served the old
// copy and a missing one degraded to `minimal`.
import minimalThemeJson from './minimal.docx.theme.json';
import devportalThemeJson from './devportal.docx.theme.json';
import vermilionThemeJson from './vermilion.docx.theme.json';
import consultingThemeJson from './consulting.docx.theme.json';

/**
 * Registry of available themes loaded from JSON files
 */
let _themesCache: Record<string, ThemeConfigJson> | null = null;

function loadThemesFromJson(): Record<string, ThemeConfigJson> {
  if (_themesCache) {
    return _themesCache;
  }

  // Build themes from imported JSON files
  const themes: Record<string, ThemeConfigJson> = {
    minimal: ensureThemeDefaults(minimalThemeJson as ThemeConfigJson),
    devportal: ensureThemeDefaults(devportalThemeJson as ThemeConfigJson),
    vermilion: ensureThemeDefaults(vermilionThemeJson as ThemeConfigJson),
    consulting: ensureThemeDefaults(consultingThemeJson as ThemeConfigJson),
  };

  // Also try to load from file system for runtime additions (if available)
  try {
    const __filename = fileURLToPath(import.meta.url);
    const __dirname = path.dirname(__filename);
    const themesDir = path.join(__dirname, '../../../dist/templates/themes');

    if (fs.existsSync(themesDir)) {
      const themeFiles = fs
        .readdirSync(themesDir)
        .filter((file) => file.endsWith('.docx.theme.json'))
        .map((file) => path.basename(file, '.docx.theme.json'));

      for (const themeName of themeFiles) {
        if (!themes[themeName]) {
          try {
            const filePath = path.join(
              themesDir,
              `${themeName}.docx.theme.json`
            );
            const content = fs.readFileSync(filePath, 'utf8');
            const parsedTheme = JSON.parse(content);
            // Check before registering. A theme that reaches the registry is
            // never validated again, so an invalid one here renders wrong
            // rather than failing — the drift that hid dead `componentDefaults`
            // props across five themes for as long as they shipped.
            if (!isValidThemeConfig(parsedTheme)) {
              console.warn(
                `Skipping theme ${themeName}: does not match the theme schema.`
              );
              continue;
            }
            themes[themeName] = ensureThemeDefaults(parsedTheme);
          } catch (error) {
            console.warn(
              `Failed to load additional theme ${themeName}:`,
              error
            );
          }
        }
      }
    }
  } catch (error) {
    // Silently ignore if file system access fails (e.g., in bundled environment)
  }

  _themesCache = themes;
  return themes;
}

export const themes = loadThemesFromJson();

/**
 * Type representing valid theme names
 */
export type ThemeName = keyof typeof themes;

/**
 * Get theme configuration by name
 * @param themeName - Name of the theme to retrieve
 * @returns Theme configuration or undefined if not found
 */
export const getTheme = (themeName: string): ThemeConfigJson | undefined => {
  return themes[themeName];
};

/**
 * Get theme configuration with safe fallback
 * @param themeName - Name of the theme to retrieve
 * @param fallbackTheme - Fallback theme name (default: 'consulting')
 * @returns Theme configuration (guaranteed to be defined)
 * @throws Error if neither theme nor fallback can be found
 */
export const getThemeWithFallback = (
  themeName: string,
  fallbackTheme: string = 'consulting'
): ThemeConfigJson => {
  const theme = getTheme(themeName) || getTheme(fallbackTheme);

  if (!theme) {
    throw new Error(
      `Failed to load theme: ${themeName}. Fallback theme '${fallbackTheme}' also not found.`
    );
  }

  return theme;
};

/**
 * Check if a theme exists
 * @param themeName - Name of the theme to check
 * @returns True if theme exists, false otherwise
 */
export const hasTheme = (themeName: string): boolean => {
  return themeName in themes;
};

/**
 * Type guard to ensure theme name is valid
 * @param themeName - Theme name to validate
 * @returns True if theme name exists in registry
 */
export const isValidThemeName = (
  themeName: string
): themeName is keyof typeof themes => {
  return hasTheme(themeName);
};

/**
 * Get all available theme names
 * @returns Array of theme names
 */
export const getThemeNames = (): string[] => {
  return Object.keys(themes);
};

// Export individual theme configs for direct access
export const minimalTheme = themes['minimal'];
export const devportalTheme = themes['devportal'];
export const vermilionTheme = themes['vermilion'];
export const consultingTheme = themes['consulting'];
